import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
import { ADMIN_PLUS, FULL_MEMBER_ROLES } from "../constants";
// eslint-disable-next-line no-restricted-imports -- legacy junk-drawer file; rename in plans/cached-singing-fiddle.md
import {
  generateId,
  normalizeMemberIds,
  normalizeWorkspaceSlug,
  assertWorkspaceSlugAvailable,
} from "./helpers";
import { assertRegionMatches } from "./region-guard";
import type { MutateSurface } from "../message-builders";
import type { MutatorCtx } from "./messages-cores";

// ── WS-B6 v2c: schema + core helper extracted from the inline mutator ──
// `server/server-mutators.ts` calls `defineMutators(clientMutators,
// overrides)` with an `actions.createWorkspaceBootstrap` override that
// runs the Core, then fires a Cloudflare-KV PUT to populate
// `workspace_id → region` immediately on workspace create. Same
// extraction shape as the messages-family cores; see `mutators.md`
// "Where side effects belong: shared mutators vs server-mutator extension".

export const createWorkspaceBootstrapSchema = z.object({
  workspaceId: z.string(),
  actorUserId: z.string(),
  name: z.string(),
  slug: z.string(),
  accessMode: z.string().optional(),
  // Mirror the user-facing /cp/workspaces caps: ≤50 entries × ≤253 chars
  // per RFC 5321. Without these here, a legacy Zero-mutator client (or a
  // future server-internal action dispatch) can push unbounded payloads
  // that the server-mutator extension then mirrors into D1.
  allowedEmailDomains: z
    .array(z.string().min(1).max(253))
    .max(50)
    .optional()
    .nullable(),
  // WS-B13a: region selector. Workspace pinning means data + zero-cache
  // for this workspace live in this region. Cross-region migration is
  // not supported, so this is effectively immutable from the user's POV.
  region: z.enum(["us", "eu"]).optional(),
});

export interface CreateWorkspaceBootstrapResult {
  workspaceId: string;
  region: "us" | "eu";
  /**
   * True when the `workspaces` row was inserted on this call. False on
   * idempotent retry (existing workspace). The server-mutator override
   * uses this to skip a redundant CF-KV write on retries — the healer
   * worker keeps drift in check, and the inline write only adds value
   * on the very first create.
   */
  created: boolean;
}

export async function createWorkspaceBootstrapCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof createWorkspaceBootstrapSchema>;
  ctx: MutatorCtx;
}): Promise<CreateWorkspaceBootstrapResult> {
  if (ctx?.sub && args.actorUserId !== ctx.sub) {
    throw new Error("Unauthorized: userId mismatch");
  }
  // WS-B6 region-equality guard: server-only. Refuse to land an
  // EU-pinned workspace in US Postgres if a misrouted client sends
  // the create here. The `ctx?.currentRegion` is set by the server
  // PushProcessor (see app-zero-sync-routes.ts) — when missing
  // (older wiring / tests) the helper is silent. Defense-in-depth
  // alongside the route-resolver Worker; the FK + auth-check layers
  // already catch most cases today, but this becomes load-bearing
  // post-WS-B7b once `workspaces` rows replicate cross-region.
  if (tx.location === "server") {
    assertRegionMatches(args.region, ctx?.currentRegion);
  }
  const normalizedSlug = normalizeWorkspaceSlug(args.slug);
  const workspaces = await tx.run(zql.workspaces);
  assertWorkspaceSlugAvailable(workspaces, normalizedSlug, args.workspaceId);
  const now = Date.now();
  const existingWorkspace = await tx.run(
    zql.workspaces.where("id", args.workspaceId).one(),
  );
  const region: "us" | "eu" = args.region ?? "us";
  const created = !existingWorkspace;
  if (created) {
    await tx.mutate.workspaces.insert({
      id: args.workspaceId,
      name: args.name,
      slug: normalizedSlug,
      created_by: args.actorUserId,
      created_at: now,
      access_mode: args.accessMode ?? "invitation",
      allowed_email_domains:
        args.accessMode === "email_domain"
          ? (args.allowedEmailDomains ?? null)
          : null,
      // Public mode defaults to off; owner flips it from access-tab.
      is_public: false,
      retention_applies_to_files: true,
      retention_applies_to_pins: true,
      region,
    });
  }

  const workspaceOwner = await tx.run(
    zql.workspace_members
      .where("workspace_id", args.workspaceId)
      .where("user_id", args.actorUserId)
      .where("removed_at", "IS", null)
      .one(),
  );
  if (!workspaceOwner) {
    // The workspace creator is its `primary_owner` (the founder, with
    // ultimate transfer authority). The distinction matters for the
    // user-lifecycle cascade (`server/user-lifecycle.ts` only acts on
    // `role === 'primary_owner'` when deciding whether to cascade-
    // delete an ownerless workspace on `user.deleted`). Without this,
    // sole creators got `'owner'`, the cascade branch never fired,
    // and deleting a user left orphan workspaces behind. Other owners
    // can still be promoted later via `workspace_members.update`.
    //
    // Explicit null on removed_at / removed_by so the upsert clears
    // the tombstone on rebootstrap (deterministic PK means the
    // existing tombstone row is the one that gets updated).
    // Otherwise Zero preserves the stale NOT-NULL values and the
    // owner row stays invisible to the replica.
    await tx.mutate.workspace_members.upsert({
      id: `${args.workspaceId}:${args.actorUserId}`,
      workspace_id: args.workspaceId,
      user_id: args.actorUserId,
      role: "primary_owner",
      joined_at: now,
      removed_at: null,
      removed_by: null,
    });
  }

  for (const channelName of ["general", "random"]) {
    const existingChannel = await tx.run(
      zql.channels
        .where("workspace_id", args.workspaceId)
        .where("name", channelName)
        .one(),
    );
    const channelId = existingChannel?.id ?? generateId();
    if (!existingChannel) {
      await tx.mutate.channels.insert({
        id: channelId,
        workspace_id: args.workspaceId,
        name: channelName,
        is_private: false,
        created_by: args.actorUserId,
        created_at: now,
        is_archived: false,
        posting_restriction: "everyone",
        // WS-B1/WS-B13a: default channels inherit the workspace region.
        region,
      });
    }

    const channelOwner = await tx.run(
      zql.channel_members
        .where("channel_id", channelId)
        .where("user_id", args.actorUserId)
        .where("removed_at", "IS", null)
        .one(),
    );
    if (!channelOwner) {
      // Explicit null clears tombstone on rebootstrap — same
      // rationale as the workspace_members upsert above.
      await tx.mutate.channel_members.upsert({
        id: `${channelId}:${args.actorUserId}`,
        channel_id: channelId,
        user_id: args.actorUserId,
        is_channel_manager: false,
        joined_at: now,
        muted: false,
        notify: "all",
        hidden: false,
        removed_at: null,
        removed_by: null,
      });
    }
  }

  return { workspaceId: args.workspaceId, region, created };
}

// Bootstrap actions — multi-step inserts that stand up a workspace or a
// channel together with its initial members in a single mutator call.
// Each is idempotent and safe to retry: existing rows are reused, and
// tombstoned membership rows are resurrected by id (never re-inserted)
// to satisfy the `channel_members_ch_user_unique` constraint.
export const bootstrapActionsMutators = {
  createChannelWithMembers: defineMutator(
    z.object({
      channelId: z.string(),
      workspaceId: z.string(),
      actorUserId: z.string(),
      name: z.string(),
      description: z.string().optional().nullable(),
      isPrivate: z.boolean().optional(),
      // Parent space (channels row with type='space'). Renamed from
      // `clientSpaceId` post migration 120. Update the caller in the
      // same PR (CreateChannelDialog.tsx).
      parentChannelId: z.string().optional().nullable(),
      memberUserIds: z.array(z.string()).optional(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.actorUserId !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      const actorMembership = await tx.run(
        zql.workspace_members
          .where("workspace_id", args.workspaceId)
          .where("user_id", args.actorUserId)
          .where("removed_at", "IS", null)
          .one(),
      );

      // Validate space-level authorization when assigning under a parent space
      if (args.parentChannelId) {
        // Verify the parent is a space in this workspace
        const space = await tx.run(
          zql.channels
            .where("id", args.parentChannelId)
            .where("type", "space")
            .where("workspace_id", args.workspaceId)
            .one(),
        );
        if (!space) {
          throw new Error("Parent space not found in this workspace");
        }

        // Space managers from linked workspaces may not be host workspace members.
        // Allow if they're a space manager (permission layer already verified this).
        if (!actorMembership) {
          const spaceMembership = await tx.run(
            zql.channel_members
              .where("channel_id", args.parentChannelId)
              .where("user_id", args.actorUserId)
              .where("removed_at", "IS", null)
              .one(),
          );
          if (!spaceMembership?.is_channel_manager) {
            throw new Error("Actor is not a member of this workspace");
          }
        } else {
          const isWsAdmin = (ADMIN_PLUS as readonly string[]).includes(
            actorMembership.role,
          );
          if (!isWsAdmin) {
            const spaceMembership = await tx.run(
              zql.channel_members
                .where("channel_id", args.parentChannelId)
                .where("user_id", args.actorUserId)
                .where("removed_at", "IS", null)
                .one(),
            );
            if (!spaceMembership?.is_channel_manager) {
              throw new Error(
                "Not authorized to create channels in this space",
              );
            }
          }
        }
      } else if (!actorMembership) {
        throw new Error("Actor is not a member of this workspace");
      }

      const channel = await tx.run(
        zql.channels.where("id", args.channelId).one(),
      );
      if (!channel) {
        // WS-B13a follow-up: child channels inherit the workspace's
        // region. Workspace pinning means data + zero-cache for this
        // workspace live in one region; mismatched child rows would
        // be unreachable from the authoritative replica.
        const workspace = await tx.run(
          zql.workspaces.where("id", args.workspaceId).one(),
        );
        // Direct table-level upsert — bypasses the public channels.insert
        // Zod schema (which intentionally excludes `parent_id` so callers
        // can't bypass this action's space-manager/admin auth checks).
        await tx.mutate.channels.upsert({
          id: args.channelId,
          workspace_id: args.workspaceId,
          parent_id: args.parentChannelId ?? undefined,
          name: args.name,
          description: args.description ?? null,
          is_private: args.isPrivate ?? false,
          created_by: args.actorUserId,
          created_at: Date.now(),
          is_archived: false,
          posting_restriction: "everyone",
          type: "channel",
          region: workspace?.region ?? "us",
        });
      }

      const defaultMemberIds =
        args.memberUserIds && args.memberUserIds.length > 0
          ? normalizeMemberIds(args.memberUserIds)
          : args.parentChannelId
            ? // Space channels: only add space members (not all workspace members)
              normalizeMemberIds(
                (
                  await tx.run(
                    zql.channel_members
                      .where("channel_id", args.parentChannelId)
                      .where("removed_at", "IS", null),
                  )
                ).map((member) => member.user_id),
              )
            : args.isPrivate
              ? // Private channels: creator only. Others must be explicitly invited.
                [args.actorUserId]
              : // Public channels: add all ACTIVE full workspace members.
                // Filter `removed_at IS NULL` so kicked members (whose row
                // is soft-deleted by `workspace_members.delete`) don't get
                // silently re-added to every new public channel — that's a
                // visibility leak: the tombstoned workspace_members row
                // keeps `role` populated, but the cascade trigger
                // (`on_workspace_member_removal`) only fires forward, not
                // on future channel creations. Without this filter the
                // user shows up in the new channel's `channel_members`
                // with `removed_at = NULL`, the publication filter lets
                // it through to their client, and the inbox query
                // (`messages.inboxForUser` via `channelMemberGuard`)
                // surfaces every subsequent message. Collaborators are
                // channel-scoped and must be invited to specific channels.
                normalizeMemberIds(
                  (
                    await tx.run(
                      zql.workspace_members
                        .where("workspace_id", args.workspaceId)
                        .where("role", "IN", FULL_MEMBER_ROLES)
                        .where("removed_at", "IS", null),
                    )
                  ).map((member) => member.user_id),
                );

      for (const userId of defaultMemberIds) {
        // Same tombstone resurrection pattern as the system-log
        // channel path above — look up by unique (channel_id,
        // user_id), update-by-id if tombstone exists so we don't
        // collide with channel_members_ch_user_unique.
        const existingMembership = await tx.run(
          zql.channel_members
            .where("channel_id", args.channelId)
            .where("user_id", userId)
            .one(),
        );
        if (existingMembership && existingMembership.removed_at == null) {
          continue;
        }
        if (existingMembership && existingMembership.removed_at != null) {
          await tx.mutate.channel_members.update({
            id: existingMembership.id,
            is_channel_manager: false,
            joined_at: Date.now(),
            muted: false,
            notify: "all",
            hidden: false,
            removed_at: null,
            removed_by: null,
          });
          continue;
        }
        await tx.mutate.channel_members.insert({
          id: generateId(),
          channel_id: args.channelId,
          user_id: userId,
          is_channel_manager: false,
          joined_at: Date.now(),
          muted: false,
          notify: "all",
          hidden: false,
        });
      }
    },
  ),

  // WS-B6 v2c: thin wrapper around `createWorkspaceBootstrapCore`. The
  // server-mutator override in `server/server-mutators.ts` calls the same
  // Core, then fires a Cloudflare-KV PUT to populate `workspace_id → region`
  // immediately. This client wrapper discards the Core's return value —
  // the result type only matters server-side.
  createWorkspaceBootstrap: defineMutator(
    createWorkspaceBootstrapSchema,
    async (params) => {
      await createWorkspaceBootstrapCore(params);
    },
  ),
};
